import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  // useNavigate,
  useParams,
} from "react-router-dom";
import type { Node } from "reactflow";
import { FiPlus, FiSidebar, FiX } from "react-icons/fi";

import EnsembleCanvas from "./components/Canvas/EnsembleCanvas";
import CBCDetailsPanel from "./components/RightPanel/CBCDetailsPanel";
import { useEnsembleData } from "./hooks/useEnsembleData";
import type {
  CBCFormData,
  CBCNodeData,
  CanvasPosition,
  EnsembleCBC,
} from "./types/ensemble.types";
import {
  deleteCbcDetails,
  getEnsembleModellingDetails,
  postCbcDetails,
  postEnsembleModelCbcRelation,
  putCbcDetails,
  saveEnsembleRelationNotes,
} from "../../services/ensembleModelling.service";
import type { IGetEnsembleModellingInterface } from "../../interfaces/ensembleModelling.interfaces";
import { getScopeEnvironmentId } from "../../services/activeScope";

const toPositiveNumber = (value?: string | null): number | null => {
  if (value == null || value === "") {
    return null;
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
};

const isPositiveNumber = (value: number | null | undefined): value is number =>
  typeof value === "number" && Number.isFinite(value) && value > 0;

type FilterFieldKey = "" | "ensemble";

interface AppliedFilter {
  field: "ensemble";
  value: string;
}

export interface EnsembleModellingV2CreateControls {
  triggerCreate: () => void;
  openFilter: () => void;
  canCreate: boolean;
  isCreating: boolean;
}

export interface EnsembleModellingV2Props {
  hidePrimaryHeader?: boolean;
  onCreateControlsChange?: (
    controls: EnsembleModellingV2CreateControls
  ) => void;
}

export const EnsembleModellingV2: React.FC<EnsembleModellingV2Props> = ({
  hidePrimaryHeader = false,
  onCreateControlsChange,
}) => {
  const { environmentId: environmentIdParam, modelId: modelIdParam } =
    useParams<{ environmentId?: string; modelId?: string }>();
  // const navigate = useNavigate();

  const fallbackEnvironmentIdRaw = useMemo(
    () => getScopeEnvironmentId(),
    []
  );

  const lastVisitedModelIdRaw = useMemo(
    () => localStorage.getItem("last_ensemble_model_id"),
    []
  );

  const envId = useMemo(() => {
    const fromParams = toPositiveNumber(environmentIdParam ?? null);
    const fallback = toPositiveNumber(fallbackEnvironmentIdRaw);
    return fromParams ?? fallback ?? null;
  }, [environmentIdParam, fallbackEnvironmentIdRaw]);

  const modelIdFromParams = useMemo(
    () => toPositiveNumber(modelIdParam ?? null),
    [modelIdParam]
  );

  const [selectedModelId, setSelectedModelId] = useState<number | null>(
    modelIdFromParams ?? null
  );
  const [selectedCBCId, setSelectedCBCId] = useState<number | null>(null);
  const [isCreatingCBC, setIsCreatingCBC] = useState<boolean>(false);
  const [previousSelectedCBCId, setPreviousSelectedCBCId] = useState<
    number | null
  >(null);
  const [persistedNodes, setPersistedNodes] = useState<Node<CBCNodeData>[]>([]);
  const [saveStatus, setSaveStatus] = useState<
    "idle" | "saving" | "success" | "error"
  >("idle");
  const [saveMessage, setSaveMessage] = useState<string | null>(null);
  const [isDeletingCBC, setIsDeletingCBC] = useState<boolean>(false);
  const [relationNotesOverrides, setRelationNotesOverrides] = useState<
    Map<
      number,
      { text: string; sourceId: number; targetId: number }
    >
  >(new Map());
  const [relationNotesStatus, setRelationNotesStatus] = useState<
    "idle" | "saving" | "success" | "error"
  >("idle");
  const [relationNotesMessage, setRelationNotesMessage] = useState<
    string | null
  >(null);
  const [isDetailsPanelOpen, setDetailsPanelOpen] = useState<boolean>(false);
  const [isFilterPanelOpen, setFilterPanelOpen] = useState<boolean>(false);
  const [filterField, setFilterField] = useState<FilterFieldKey>("");
  const [filterValueDraft, setFilterValueDraft] = useState<string>("");
  const [appliedFilter, setAppliedFilter] = useState<AppliedFilter | null>(
    null
  );

  const canTriggerCreate = useMemo(
    () => isPositiveNumber(selectedModelId) && !isCreatingCBC,
    [selectedModelId, isCreatingCBC]
  );

  const [models, setModels] = useState<IGetEnsembleModellingInterface[]>([]);
  const [modelsLoading, setModelsLoading] = useState<boolean>(true);
  const [modelsError, setModelsError] = useState<string | null>(null);

  useEffect(() => {
    if (modelIdFromParams) {
      setSelectedModelId(modelIdFromParams);
    }
  }, [modelIdFromParams]);

  useEffect(() => {
    let isMounted = true;

    const loadModels = async () => {
      if (!isPositiveNumber(envId)) {
        setModels([]);
        setModelsLoading(false);
        setModelsError(
          "Missing environment context. Please select a workspace to continue."
        );
        return;
      }

      setModelsLoading(true);
      setModelsError(null);
      try {
        const response = await getEnsembleModellingDetails();
        if (!isMounted) return;

        const sorted = [...response].sort((a, b) =>
          (a?.ensemble_model_name ?? "").localeCompare(
            b?.ensemble_model_name ?? "",
            undefined,
            { sensitivity: "base" }
          )
        );

        setModels(sorted);

        if (sorted.length > 0) {
          const preferredModelId =
            modelIdFromParams ??
            (() => {
              const stored = toPositiveNumber(lastVisitedModelIdRaw);
              return stored &&
                sorted.some(
                  (model) => model.ensemble_model_id === stored
                )
                ? stored
                : null;
            })();

          const initialModelId =
            preferredModelId ?? sorted[0].ensemble_model_id;

          setSelectedModelId((current) =>
            isPositiveNumber(current) ? current : initialModelId
          );
        }
      } catch (err) {
        if (!isMounted) return;
        setModels([]);
        setModelsError(
          err instanceof Error
            ? err.message
            : "Failed to load ensemble models."
        );
      } finally {
        if (isMounted) {
          setModelsLoading(false);
        }
      }
    };

    void loadModels();

    return () => {
      isMounted = false;
    };
  }, [envId, lastVisitedModelIdRaw, modelIdFromParams]);

  useEffect(() => {
    if (isPositiveNumber(selectedModelId)) {
      localStorage.setItem(
        "last_ensemble_model_id",
        String(selectedModelId)
      );
    }
  }, [selectedModelId]);

  useEffect(() => {
    setSelectedCBCId(null);
    setSaveStatus("idle");
    setSaveMessage(null);
    setPersistedNodes([]);
    setIsCreatingCBC(false);
    setPreviousSelectedCBCId(null);
    setIsDeletingCBC(false);
    setDetailsPanelOpen(false);
    setFilterPanelOpen(false);
    setFilterField("");
    setFilterValueDraft("");
    setAppliedFilter(null);
  }, [selectedModelId]);

  const { cbcs, relations, loading, error, refetch } = useEnsembleData(
    envId ?? 0,
    selectedModelId ?? 0,
    { enabled: isPositiveNumber(envId) && isPositiveNumber(selectedModelId) }
  );

  const relationsById = useMemo(() => {
    return new Map(
      relations.map((relation) => [
        relation.ensemble_model_cbc_relation_id,
        relation,
      ])
    );
  }, [relations]);

  useEffect(() => {
    setRelationNotesOverrides((prev) => {
      let changed = false;
      const next = new Map(prev);
      Array.from(next.keys()).forEach((key) => {
        if (!relationsById.has(key)) {
          next.delete(key);
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [relationsById]);

  const relationsWithNotes = useMemo(() => {
    return relations.map((relation) => {
      const override = relationNotesOverrides.get(
        relation.ensemble_model_cbc_relation_id
      );
      return {
        ...relation,
        relation_description:
          override?.text ?? relation.relation_description ?? "",
      };
    });
  }, [relations, relationNotesOverrides]);

  const filteredCbcs = useMemo(() => {
    if (!appliedFilter || appliedFilter.field !== "ensemble") {
      return cbcs;
    }
    const needle = appliedFilter.value.trim().toLowerCase();
    if (!needle) {
      return cbcs;
    }
    return cbcs.filter((cbc) =>
      (cbc.ensemble_name ?? "").toLowerCase().includes(needle)
    );
  }, [appliedFilter, cbcs]);

  const filteredRelationsWithNotes = useMemo(() => {
    if (filteredCbcs.length === cbcs.length) {
      return relationsWithNotes;
    }
    const visibleIds = new Set(
      filteredCbcs.map((cbc) => cbc.ensemble_model_cbc_id)
    );
    return relationsWithNotes.filter(
      (relation) =>
        visibleIds.has(relation.src_ensemble_model_cbc_id) &&
        visibleIds.has(relation.tgt_ensemble_model_cbc_id)
    );
  }, [cbcs.length, filteredCbcs, relationsWithNotes]);

  const selectedCBC = useMemo(
    () =>
      filteredCbcs.find(
        (cbc) => cbc.ensemble_model_cbc_id === selectedCBCId
      ) ?? null,
    [filteredCbcs, selectedCBCId]
  );

  useEffect(() => {
    if (isCreatingCBC) {
      return;
    }

    if (filteredCbcs.length === 0) {
      setSelectedCBCId(null);
      setDetailsPanelOpen(false);
      return;
    }

    setSelectedCBCId((current) => {
      if (
        current &&
        filteredCbcs.some((cbc) => cbc.ensemble_model_cbc_id === current)
      ) {
        return current;
      }
      return filteredCbcs[0].ensemble_model_cbc_id;
    });
  }, [filteredCbcs, isCreatingCBC]);

  const activeModel = useMemo(
    () =>
      models.find(
        (model) => model.ensemble_model_id === selectedModelId
      ) ?? null,
    [models, selectedModelId]
  );

  const handleCBCSelect = useCallback((id: number) => {
    setIsCreatingCBC(false);
    setPreviousSelectedCBCId(null);
    setSelectedCBCId(id);
  }, []);

  const handleNodesPersist = useCallback((nodes: Node<CBCNodeData>[]) => {
    setPersistedNodes(nodes);
  }, []);

  const handleEdgeLabelDraft = useCallback(
    (
      relationId: number,
      text: string,
      meta: { sourceId: number; targetId: number }
    ) => {
      if (!Number.isFinite(relationId)) {
        return;
      }
      setRelationNotesOverrides((prev) => {
        const relation = relationsById.get(relationId);
        if (!relation) {
          return prev;
        }
        const trimmed = text.trim();
        const original = (relation.relation_description ?? "").trim();
        const next = new Map(prev);
        if (trimmed === original) {
          if (next.has(relationId)) {
            next.delete(relationId);
            return next;
          }
          return prev;
        }
        next.set(relationId, {
          text: trimmed,
          sourceId:
            meta?.sourceId ?? relation.src_ensemble_model_cbc_id ?? 0,
          targetId:
            meta?.targetId ?? relation.tgt_ensemble_model_cbc_id ?? 0,
        });
        return next;
      });
    },
    [relationsById]
  );

  const handleSaveRelationNotes = useCallback(async () => {
    if (relationNotesOverrides.size === 0) {
      return;
    }
    setRelationNotesStatus("saving");
    setRelationNotesMessage("Saving relation notes…");
    try {
      const payload = Array.from(relationNotesOverrides.entries()).map(
        ([relationId, entry]) => ({
          src_ensemble_model_cbc_id: entry.sourceId,
          tgt_ensemble_model_cbc_id: entry.targetId,
          ensemble_model_cbc_relation_id: relationId,
          relation_description: entry.text,
        })
      );
      await saveEnsembleRelationNotes(payload);
      setRelationNotesStatus("success");
      setRelationNotesMessage("Relation notes saved");
      setRelationNotesOverrides(new Map());
      void refetch();
    } catch (err) {
      const message =
        err instanceof Error
          ? err.message
          : "Failed to save relation notes.";
      setRelationNotesStatus("error");
      setRelationNotesMessage(message);
    }
  }, [refetch, relationNotesOverrides]);

  const syncNodePositions = useCallback(
    async (excludeIds: number[] = []) => {
      if (!isPositiveNumber(selectedModelId) || persistedNodes.length === 0) {
        return;
      }

      const excludeSet = new Set(excludeIds);
      const cbcById = new Map<number, EnsembleCBC>(
        cbcs.map((cbc) => [cbc.ensemble_model_cbc_id, cbc])
      );

      const updates = persistedNodes
        .map((node) => {
          const nodeId = Number(node.id);
          if (!Number.isFinite(nodeId) || excludeSet.has(nodeId)) {
            return null;
          }
          const cbc = cbcById.get(nodeId);
          if (!cbc) {
            return null;
          }

          const position: CanvasPosition = {
            x: Math.round(node.position?.x ?? 0),
            y: Math.round(node.position?.y ?? 0),
          };

          if (
            Math.abs(position.x - cbc.canvas_position.x) < 1 &&
            Math.abs(position.y - cbc.canvas_position.y) < 1
          ) {
            return null;
          }

          return { cbc, position };
        })
        .filter(
          (entry): entry is { cbc: EnsembleCBC; position: CanvasPosition } =>
            entry !== null
        );

      if (updates.length === 0) {
        return;
      }

      await Promise.all(
        updates.map(({ cbc, position }) => {
          const payload = {
            ensemble_model_cbc_name: cbc.ensemble_model_cbc_name,
            ensemble_model_cbc_type: cbc.ensemble_model_cbc_type,
            ensemble_name: cbc.ensemble_name,
            business_keys: cbc.business_keys,
            description: cbc.description,
            canvas_position: position,
          };

          return putCbcDetails(
            payload,
            selectedModelId,
            cbc.ensemble_model_cbc_id
          );
        })
      );
    },
    [cbcs, persistedNodes, selectedModelId]
  );

  // Persist a single node's position as soon as the user finishes dragging it.
  // The position comes straight from the drag event, so this does not depend on
  // the `persistedNodes` mirror being in sync. Locally-committed positions are
  // tracked in a ref so a second drag of the same node diffs against the value
  // we just saved (we deliberately don't refetch here — the node is already
  // where the user dropped it, and refetching mid-interaction would snap it).
  const committedPositionsRef = useRef<Map<number, CanvasPosition>>(new Map());

  const handleNodePositionCommit = useCallback(
    async (cbcId: number, position: CanvasPosition) => {
      if (!isPositiveNumber(selectedModelId)) {
        return;
      }
      const cbc = cbcs.find((entry) => entry.ensemble_model_cbc_id === cbcId);
      if (!cbc) {
        return;
      }

      const lastKnown =
        committedPositionsRef.current.get(cbcId) ?? cbc.canvas_position;
      if (
        Math.abs(position.x - lastKnown.x) < 1 &&
        Math.abs(position.y - lastKnown.y) < 1
      ) {
        return;
      }

      committedPositionsRef.current.set(cbcId, position);

      try {
        await putCbcDetails(
          {
            ensemble_model_cbc_name: cbc.ensemble_model_cbc_name,
            ensemble_model_cbc_type: cbc.ensemble_model_cbc_type,
            ensemble_name: cbc.ensemble_name,
            business_keys: cbc.business_keys,
            description: cbc.description,
            canvas_position: position,
          },
          selectedModelId,
          cbcId
        );
      } catch (err) {
        // Roll back the optimistic record so a retry re-sends, and surface it.
        committedPositionsRef.current.delete(cbcId);
        setSaveStatus("error");
        setSaveMessage(
          err instanceof Error
            ? err.message
            : "Failed to save the new position. Please try again."
        );
      }
    },
    [cbcs, selectedModelId]
  );

  // Clear committed-position memory when the model changes so positions from
  // one model can't leak into the diff for another.
  useEffect(() => {
    committedPositionsRef.current = new Map();
  }, [selectedModelId]);

  // const handleModelSelect = useCallback(
  //   (event: React.ChangeEvent<HTMLSelectElement>) => {
  //     const nextId = toPositiveNumber(event.target.value);
  //     if (!isPositiveNumber(nextId)) {
  //       return;
  //     }
  //     setSelectedModelId(nextId);
  //   },
  //   []
  // );

  // const handleBack = useCallback(() => {
  //   if (window.history.length > 1) {
  //     navigate(-1);
  //   } else {
  //     navigate("/importDataModel");
  //   }
  // }, [navigate]);

  const handleUpdateCBC = useCallback(
    async (data: CBCFormData) => {
      if (!isPositiveNumber(envId) || !isPositiveNumber(selectedModelId)) {
        setSaveStatus("error");
        setSaveMessage("Missing ensemble identifiers. Unable to save.");
        return;
      }

      if (!selectedCBC) {
        setSaveStatus("error");
        setSaveMessage("Select a Core Business Concept before saving changes.");
        return;
      }

      setSaveStatus("saving");
      setSaveMessage(null);

      const nodePosition = persistedNodes.find(
        (node) => Number(node.id) === selectedCBC.ensemble_model_cbc_id
      );

      const canvasPosition =
        nodePosition?.position ?? selectedCBC.canvas_position;

      const payload = {
        ensemble_model_cbc_name: data.cbcName.trim(),
        ensemble_model_cbc_type: data.type,
        ensemble_name: data.ensembleName.trim(),
        business_keys: data.businessKeys.trim(),
        description: data.description.trim(),
        canvas_position: canvasPosition,
      };

      try {
        await putCbcDetails(
          payload,
          selectedModelId,
          selectedCBC.ensemble_model_cbc_id
        );
        await syncNodePositions([selectedCBC.ensemble_model_cbc_id]);
        setSaveStatus("success");
        setSaveMessage("Core Business Concept updated successfully.");
        await refetch();
      } catch (err) {
        const message =
          err instanceof Error
            ? err.message
            : "Failed to save Core Business Concept changes. Please try again.";
        setSaveStatus("error");
        setSaveMessage(message);
      }
    },
    [envId, persistedNodes, refetch, selectedCBC, selectedModelId, syncNodePositions]
  );

  const handleCreateCBC = useCallback(
    async (data: CBCFormData) => {
      if (!isPositiveNumber(envId) || !isPositiveNumber(selectedModelId)) {
        setSaveStatus("error");
        setSaveMessage("Missing ensemble identifiers. Unable to create Core Business Concept.");
        return;
      }

      const trimmedName = data.cbcName.trim();
      const trimmedEnsemble = data.ensembleName.trim();
      const trimmedKeys = data.businessKeys.trim();

      const validationErrors: string[] = [];

      if (!trimmedName) {
        validationErrors.push("Core Business Concept name is required.");
      }
      if (!trimmedEnsemble) {
        validationErrors.push("Ensemble name is required.");
      }
      if (!trimmedKeys) {
        validationErrors.push("Business keys are required.");
      }

      if (validationErrors.length > 0) {
        setSaveStatus("error");
        setSaveMessage(validationErrors.join(" "));
        return;
      }

      setSaveStatus("saving");
      setSaveMessage(null);

      const payload = {
        ensemble_model_cbc_name: trimmedName,
        ensemble_model_cbc_type: data.type,
        ensemble_name: trimmedEnsemble,
        business_keys: trimmedKeys,
        description: data.description.trim(),
        canvas_position: { x: 0, y: 0 },
      };

      try {
        await postCbcDetails(payload, selectedModelId);
        setSaveStatus("success");
        setSaveMessage("Core Business Concept created successfully.");
        setIsCreatingCBC(false);
        setPreviousSelectedCBCId(null);
        setSelectedCBCId(null);
        setDetailsPanelOpen(false);
        await refetch();
      } catch (err) {
        const message =
          err instanceof Error
            ? err.message
            : "Failed to create Core Business Concept. Please try again.";
        setSaveStatus("error");
        setSaveMessage(message);
      }
    },
    [envId, refetch, selectedModelId]
  );

  const handleRelationDeleted = useCallback(
    async (_relationId: number) => {
      await refetch();
    },
    [refetch]
  );

  const handleRelationCreate = useCallback(
    async (sourceId: number, targetId: number) => {
      if (!isPositiveNumber(envId) || !isPositiveNumber(selectedModelId)) {
        const message =
          "Select an ensemble model before creating dependencies.";
        setSaveStatus("error");
        setSaveMessage(message);
        throw new Error(message);
      }

      setSaveMessage(null);

      try {
        await postEnsembleModelCbcRelation(
          {
            src_ensemble_model_cbc_id: sourceId,
            tgt_ensemble_model_cbc_id: targetId,
          },
          selectedModelId
        );
        setSaveStatus("success");
        setSaveMessage("Dependency created successfully.");
        // await refetch();
      } catch (err) {
        const message =
          err instanceof Error
            ? err.message
            : "Failed to create dependency. Please try again.";
        setSaveStatus("error");
        setSaveMessage(message);
        throw err instanceof Error ? err : new Error(message);
      }
    },
    [envId, refetch, relations, selectedModelId]
  );

  const handleCancel = useCallback(() => {
    if (isCreatingCBC) {
      setIsCreatingCBC(false);
      setSelectedCBCId((current) => {
        if (previousSelectedCBCId) {
          return previousSelectedCBCId;
        }
        if (current) {
          return current;
        }
        return cbcs.length > 0 ? cbcs[0].ensemble_model_cbc_id : null;
      });
      setPreviousSelectedCBCId(null);
    } else {
      setSelectedCBCId(null);
    }
    setSaveStatus("idle");
    setSaveMessage(null);
    setIsDeletingCBC(false);
    setDetailsPanelOpen(false);
  }, [cbcs, isCreatingCBC, previousSelectedCBCId]);

  const handleCreateNewCBC = useCallback(() => {
    if (!isPositiveNumber(selectedModelId)) {
      setSaveStatus("error");
      setSaveMessage("Select an ensemble model before creating a Core Business Concept.");
      return;
    }
    setSaveStatus("idle");
    setSaveMessage(null);
    setPreviousSelectedCBCId(selectedCBCId);
    setSelectedCBCId(null);
    setIsCreatingCBC(true);
    setPersistedNodes([]);
    setIsDeletingCBC(false);
    setDetailsPanelOpen(true);
  }, [selectedModelId, selectedCBCId]);

  const handleFilterApply = useCallback(() => {
    if (filterField !== "ensemble") {
      setAppliedFilter(null);
      setFilterPanelOpen(false);
      return;
    }

    const value = filterValueDraft.trim();
    if (!value) {
      setAppliedFilter(null);
      setFilterPanelOpen(false);
      return;
    }

    setAppliedFilter({
      field: "ensemble",
      value,
    });
    setFilterPanelOpen(false);
  }, [filterField, filterValueDraft]);

  const handleFilterClear = useCallback(() => {
    setFilterField("");
    setFilterValueDraft("");
    setAppliedFilter(null);
  }, []);

  useEffect(() => {
    if (!onCreateControlsChange) {
      return;
    }

    onCreateControlsChange({
      triggerCreate: handleCreateNewCBC,
      openFilter: () => setFilterPanelOpen(true),
      canCreate: canTriggerCreate,
      isCreating: isCreatingCBC,
    });
  }, [
    canTriggerCreate,
    handleCreateNewCBC,
    isCreatingCBC,
    onCreateControlsChange,
  ]);

  const handleDeleteCBC = useCallback(async () => {
    if (!isPositiveNumber(selectedModelId) || !selectedCBC) {
      setSaveStatus("error");
      setSaveMessage("Select a Core Business Concept before attempting deletion.");
      return;
    }

    const confirmDelete = window.confirm(
      `Delete "${selectedCBC.ensemble_model_cbc_name}" and its relationships?`
    );
    if (!confirmDelete) {
      return;
    }

    setIsDeletingCBC(true);
    setSaveStatus("saving");
    setSaveMessage(null);

    try {
      await deleteCbcDetails(
        selectedModelId,
        selectedCBC.ensemble_model_cbc_id
      );
      setSaveStatus("success");
      setSaveMessage("Core Business Concept deleted successfully.");
      setIsCreatingCBC(false);
      setPreviousSelectedCBCId(null);
      setSelectedCBCId(null);
      setPersistedNodes([]);
      setDetailsPanelOpen(false);
      await refetch();
    } catch (err) {
      const message =
        err instanceof Error
          ? err.message
          : "Failed to delete Core Business Concept. Please try again.";
      setSaveStatus("error");
      setSaveMessage(message);
    } finally {
      setIsDeletingCBC(false);
    }
  }, [refetch, selectedCBC, selectedModelId]);

  useEffect(() => {
    if (saveStatus === "success" || saveStatus === "error") {
      const timeout = window.setTimeout(() => {
        setSaveStatus("idle");
        setSaveMessage(null);
      }, 4000);

      return () => window.clearTimeout(timeout);
    }

    return undefined;
  }, [saveStatus]);

  useEffect(() => {
    if (relationNotesStatus === "success" || relationNotesStatus === "error") {
      const timeout = window.setTimeout(() => {
        setRelationNotesStatus("idle");
        setRelationNotesMessage(null);
      }, 4000);
      return () => window.clearTimeout(timeout);
    }
    return undefined;
  }, [relationNotesStatus]);

  const statusSummary =
    saveStatus === "success" && saveMessage
      ? { type: "success" as const, message: saveMessage }
      : saveStatus === "error" && saveMessage
      ? { type: "error" as const, message: saveMessage }
      : null;
  const hasPendingRelationNotes = relationNotesOverrides.size > 0;
  const showEmptyCBCState = filteredCbcs.length === 0 && !isCreatingCBC;
  const hasDetailsTarget = Boolean(selectedCBC) || isCreatingCBC;
  const detailsPanelVisible = isDetailsPanelOpen && hasDetailsTarget;
  const filterActiveLabel = appliedFilter
    ? `${appliedFilter.field}: ${appliedFilter.value}`
    : null;

  if (!isPositiveNumber(envId)) {
    return (
      <section className="tw-flex tw-h-full tw-w-full tw-items-center tw-justify-center tw-bg-slate-50">
        <div className="tw-max-w-xl tw-rounded-xl tw-border tw-border-amber-200 tw-bg-white tw-px-6 tw-py-4 tw-text-amber-600 tw-shadow">
          <p className="tw-text-base tw-font-semibold">
            Environment context required
          </p>
          <p className="tw-mt-2 tw-text-sm tw-text-amber-500">
            Select an environment before launching Ensemble Modelling to enable
            data loading.
          </p>
        </div>
      </section>
    );
  }

  if (modelsLoading) {
    return (
      <section className="tw-flex tw-h-full tw-w-full tw-items-center tw-justify-center">
        <p className="tw-text-base tw-font-medium tw-text-slate-600">
          Loading ensemble models…
        </p>
      </section>
    );
  }

  if (modelsError) {
    return (
      <section className="tw-flex tw-h-full tw-w-full tw-items-center tw-justify-center">
        <div className="tw-rounded-xl tw-border tw-border-red-200 tw-bg-red-50 tw-px-6 tw-py-4 tw-text-red-600 tw-shadow-sm">
          <p className="tw-font-semibold">Unable to load ensemble models.</p>
          <p className="tw-text-sm tw-text-red-500">{modelsError}</p>
        </div>
      </section>
    );
  }

  if (!isPositiveNumber(selectedModelId)) {
    return (
      <section className="tw-flex tw-h-full tw-w-full tw-items-center tw-justify-center tw-bg-slate-50">
        <div className="tw-max-w-xl tw-rounded-xl tw-border tw-border-amber-200 tw-bg-white tw-px-6 tw-py-4 tw-text-amber-600 tw-shadow">
          <p className="tw-text-base tw-font-semibold">
            No ensemble models available
          </p>
          <p className="tw-mt-2 tw-text-sm tw-text-amber-500">
            Create an ensemble model in the legacy workspace to begin visualising
            relationships here.
          </p>
        </div>
      </section>
    );
  }

  if (loading) {
    return (
      <section className="tw-flex tw-h-full tw-w-full tw-items-center tw-justify-center">
        <p className="tw-text-base tw-font-medium tw-text-slate-600">
          Loading ensemble modelling workspace…
        </p>
      </section>
    );
  }

  if (error) {
    return (
      <section className="tw-flex tw-h-full tw-w-full tw-items-center tw-justify-center">
        <div className="tw-rounded-xl tw-border tw-border-red-200 tw-bg-red-50 tw-px-6 tw-py-4 tw-text-red-600 tw-shadow-sm">
          <p className="tw-font-semibold">Unable to load ensemble model.</p>
          <p className="tw-text-sm tw-text-red-500">{error}</p>
        </div>
      </section>
    );
  }

  return (
    <>
      {!hidePrimaryHeader && (
        <div className="tw-flex tw-w-full tw-flex-wrap tw-items-center tw-justify-between tw-gap-4">
          <div>
            <p className="tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wide tw-text-blue-600">
              Core Business Concepts
            </p>
            <h1 className="tw-mt-1 tw-text-2xl tw-font-semibold tw-text-slate-900">
              Ensemble Modelling
            </h1>
          </div>
          <div className="tw-flex tw-flex-wrap tw-items-center tw-justify-end tw-gap-3">
            <div className="tw-flex tw-flex-col tw-items-end tw-gap-1">
              <button
                type="button"
                onClick={handleSaveRelationNotes}
                className={`tw-rounded-lg tw-border tw-border-slate-200 tw-bg-white tw-px-4 tw-py-2 tw-text-sm tw-font-semibold tw-text-[#03234a] tw-transition ${
                  hasPendingRelationNotes
                    ? "hover:tw-bg-indigo-50"
                    : "tw-cursor-not-allowed tw-opacity-50"
                }`}
                disabled={
                  !hasPendingRelationNotes || relationNotesStatus === "saving"
                }
              >
                {relationNotesStatus === "saving"
                  ? "Saving notes…"
                  : "Save relation notes"}
              </button>
              {relationNotesMessage && relationNotesStatus !== "idle" && (
                <span
                  className={`tw-text-[0.65rem] ${
                    relationNotesStatus === "error"
                      ? "tw-text-red-500"
                      : "tw-text-emerald-600"
                  }`}
                >
                  {relationNotesMessage}
                </span>
              )}
              {!detailsPanelVisible && hasDetailsTarget && (
                <button
                  type="button"
                  onClick={() => setDetailsPanelOpen(true)}
                  className="tw-mt-3 tw-inline-flex tw-items-center tw-gap-2 tw-rounded-full tw-border tw-border-[#03234a]/10 tw-bg-white tw-px-4 tw-py-2 tw-text-[0.6rem] tw-font-semibold tw-uppercase tw-tracking-[0.3em] tw-text-[#03234a] tw-shadow-[0_18px_40px_-26px_rgba(3,35,74,0.45)] tw-transition hover:tw-translate-y-[-1px] hover:tw-shadow-[0_24px_46px_-28px_rgba(3,35,74,0.55)]"
                  aria-label="Expand Core Business Concept details"
                >
                  <FiSidebar />
                  Details
                </button>
              )}
            </div>
            <button
              type="button"
              onClick={handleCreateNewCBC}
              className={`tw-inline-flex tw-items-center tw-gap-2 tw-rounded-lg tw-bg-[#03234a] tw-px-4 tw-py-2 tw-text-sm tw-font-semibold tw-text-white tw-transition focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-offset-2 focus:tw-ring-[#03234a]/60 ${
                canTriggerCreate
                  ? "hover:tw-bg-[#0a3f7f] hover:tw-shadow-lg"
                  : "tw-cursor-not-allowed tw-opacity-60 hover:tw-bg-[#03234a]"
              }`}
              disabled={!canTriggerCreate}
            >
              <FiPlus className="tw-h-4 tw-w-4" />
              New concept
            </button>
          </div>
        </div>
      )}

      <main
        className="tw-relative tw-flex tw-w-full tw-flex-1 tw-min-h-0"
        style={{ minHeight: "24rem" }}
      >
        {showEmptyCBCState ? (
          <div className="tw-flex tw-w-full tw-flex-1 tw-flex-col tw-items-center tw-justify-center tw-rounded-[2.25rem] tw-border tw-border-dashed tw-border-slate-200 tw-bg-white tw-p-8 tw-text-center tw-text-slate-600">
            <p className="tw-text-lg tw-font-semibold">
              {filterActiveLabel
                ? "No Core Business Concepts match this filter"
                : "No Core Business Concepts yet"}
            </p>
            <p className="tw-mt-2 tw-text-sm tw-text-slate-500 tw-max-w-md">
              {filterActiveLabel
                ? "Try a different ensemble value or clear the filter to view all records."
                : "Start by creating your first Core Business Concept to visualise relationships in this ensemble model."}
            </p>
          </div>
        ) : (
          <div className="tw-relative tw-flex tw-h-full tw-w-full" style={{height:"80vh"}}>
            <div className="tw-flex tw-w-full tw-flex-1 tw-min-h-[24rem] tw-rounded-[2.25rem] tw-border tw-border-slate-100 tw-bg-white tw-p-4 tw-shadow-[0_30px_80px_-50px_rgba(15,23,42,0.55)]">
              <div className="tw-relative tw-flex tw-h-full tw-min-h-[24rem] tw-w-full tw-flex-1 tw-overflow-hidden tw-rounded-[1.75rem] tw-bg-[#f6f8ff]">
                {hidePrimaryHeader && (
                  <div className="tw-pointer-events-none tw-absolute tw-right-4 tw-top-4 tw-z-10">
                    <div className="tw-pointer-events-auto tw-flex tw-flex-col tw-items-end tw-gap-1">
                      <button
                        type="button"
                        onClick={handleSaveRelationNotes}
                        className={`tw-rounded-lg tw-border tw-border-slate-200 tw-bg-white tw-px-4 tw-py-2 tw-text-sm tw-font-semibold tw-text-[#03234a] tw-transition ${
                          hasPendingRelationNotes
                            ? "hover:tw-bg-indigo-50"
                            : "tw-cursor-not-allowed tw-opacity-50"
                        }`}
                        disabled={
                          !hasPendingRelationNotes ||
                          relationNotesStatus === "saving"
                        }
                      >
                        {relationNotesStatus === "saving"
                          ? "Saving notes…"
                          : "Save relation notes"}
                      </button>
                      {relationNotesMessage && relationNotesStatus !== "idle" && (
                        <span
                          className={`tw-text-[0.65rem] ${
                            relationNotesStatus === "error"
                              ? "tw-text-red-500"
                              : "tw-text-emerald-600"
                          }`}
                        >
                          {relationNotesMessage}
                        </span>
                      )}
                      {!detailsPanelVisible && hasDetailsTarget && (
                        <button
                          type="button"
                          onClick={() => setDetailsPanelOpen(true)}
                          className="tw-mt-3 tw-inline-flex tw-items-center tw-gap-2 tw-rounded-lg tw-border tw-border-slate-200 tw-bg-white tw-px-4 tw-py-2 tw-text-sm tw-font-semibold tw-text-[#03234a] tw-shadow-sm tw-transition hover:tw-bg-slate-50"
                          aria-label="Expand Core Business Concept details"
                        >
                          <FiSidebar />
                          Details
                        </button>
                      )}
                    </div>
                  </div>
                )}
                <EnsembleCanvas
                  cbcs={filteredCbcs}
                  relations={filteredRelationsWithNotes}
                  selectedCBCId={selectedCBCId}
                  onCBCSelect={handleCBCSelect}
                  onNodesPersist={handleNodesPersist}
                  onNodePositionCommit={handleNodePositionCommit}
                  onRelationCreate={handleRelationCreate}
                  onEdgeLabelChange={handleEdgeLabelDraft}
                  className="tw-rounded-[1.75rem]"
                />
              </div>
            </div>

          </div>
        )}
      </main>

      <div
        className={`tw-fixed tw-inset-y-0 tw-right-0 tw-z-[60] tw-flex tw-w-full tw-max-w-full sm:tw-w-[24rem] tw-transform tw-transition-transform tw-duration-300 tw-ease-in-out ${
          isFilterPanelOpen ? "tw-translate-x-0" : "tw-translate-x-full"
        }`}
        aria-hidden={!isFilterPanelOpen}
      >
        <div className="tw-relative tw-flex tw-h-full tw-w-full tw-flex-col tw-border-l tw-border-slate-200 tw-bg-white tw-shadow-[0_32px_80px_-40px_rgba(15,23,42,0.75)]">
          <button
            type="button"
            onClick={() => setFilterPanelOpen(false)}
            className="tw-absolute tw-right-4 tw-top-4 tw-z-10 tw-flex tw-h-9 tw-w-9 tw-items-center tw-justify-center tw-rounded-full tw-border tw-border-slate-200 tw-bg-white tw-text-slate-600"
            aria-label="Close filter panel"
          >
            <FiX />
          </button>

          <div className="tw-flex tw-h-full tw-flex-col tw-gap-5 tw-p-6 tw-pt-16">
            <div>
              <p className="tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wide tw-text-blue-600">
                Canvas filter
              </p>
              <h3 className="tw-mt-2 tw-text-lg tw-font-semibold tw-text-slate-900">
                Filter Core Business Concepts
              </h3>
            </div>

            <label className="tw-flex tw-flex-col tw-gap-2">
              <span className="tw-text-xs tw-font-semibold tw-uppercase tw-tracking-[0.2em] tw-text-slate-600">
                Filter Field
              </span>
              <select
                value={filterField}
                onChange={(event) => {
                  const nextField = event.target.value as FilterFieldKey;
                  setFilterField(nextField);
                  if (nextField !== "ensemble") {
                    setFilterValueDraft("");
                  }
                }}
                className="tw-rounded-lg tw-border tw-border-slate-200 tw-bg-white tw-px-3 tw-py-2 tw-text-sm tw-text-slate-800 focus:tw-border-blue-400 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-blue-100"
              >
                <option value="">Select filter field</option>
                <option value="ensemble">ensemble</option>
              </select>
            </label>

            {filterField === "ensemble" && (
              <label className="tw-flex tw-flex-col tw-gap-2">
                <span className="tw-text-xs tw-font-semibold tw-text-slate-600">
                  Ensemble Value
                </span>
                <input
                  type="text"
                  value={filterValueDraft}
                  onChange={(event) => setFilterValueDraft(event.target.value)}
                  placeholder="Enter ensemble value"
                  className="tw-rounded-lg tw-border tw-border-slate-200 tw-bg-white tw-px-3 tw-py-2 tw-text-sm tw-text-slate-800 focus:tw-border-blue-400 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-blue-100"
                />
              </label>
            )}

            <div className="tw-mt-auto tw-flex tw-gap-2">
              <button
                type="button"
                onClick={handleFilterClear}
                className="tw-flex-1 tw-rounded-lg tw-border tw-border-slate-200 tw-bg-white tw-px-3 tw-py-2 tw-text-sm tw-font-semibold tw-text-slate-700 hover:tw-bg-slate-50"
              >
                Clear
              </button>
              <button
                type="button"
                onClick={handleFilterApply}
                className="tw-flex-1 tw-rounded-lg tw-bg-[#03234a] tw-px-3 tw-py-2 tw-text-sm tw-font-semibold tw-text-white hover:tw-bg-[#0a3f7f]"
              >
                OK
              </button>
            </div>
          </div>
        </div>
      </div>

      {detailsPanelVisible && (
        <div
          className="tw-fixed tw-inset-0 tw-z-40 tw-bg-slate-900/20 tw-backdrop-blur-[1px]"
          onClick={() => setDetailsPanelOpen(false)}
        />
      )}

      <div
        className={`tw-fixed tw-inset-y-0 tw-right-0 tw-z-50 tw-flex tw-w-full tw-max-w-full sm:tw-w-[32rem] tw-transform tw-transition-transform tw-duration-300 tw-ease-in-out ${
          detailsPanelVisible ? "tw-translate-x-0" : "tw-translate-x-full"
        }`}
        aria-hidden={!detailsPanelVisible}
      >
        <div className="tw-relative tw-flex tw-h-full tw-w-full tw-flex-col tw-border-l tw-border-slate-200 tw-bg-white tw-shadow-[0_40px_90px_-45px_rgba(15,23,42,0.85)]">
          <button
            type="button"
            onClick={() => setDetailsPanelOpen(false)}
            className="tw-absolute tw-right-4 tw-top-4 tw-z-10 tw-flex tw-h-10 tw-w-10 tw-items-center tw-justify-center tw-rounded-full tw-border tw-border-slate-200 tw-bg-white tw-text-slate-600 tw-shadow"
            aria-label="Collapse Core Business Concept details"
          >
            <FiX className="tw-text-lg" />
          </button>
          <div className="tw-flex-1 tw-overflow-y-auto tw-p-4 sm:tw-p-6">
            <CBCDetailsPanel
              className="tw-flex-1"
              mode={isCreatingCBC ? "create" : "edit"}
              selectedCBC={isCreatingCBC ? null : selectedCBC ?? null}
              onSave={isCreatingCBC ? handleCreateCBC : handleUpdateCBC}
              onCancel={handleCancel}
              isSaving={saveStatus === "saving"}
              status={statusSummary}
              modelName={activeModel?.ensemble_model_name ?? ""}
              modelSubjectArea={activeModel?.subject_area ?? ""}
              cbcs={filteredCbcs}
              relations={filteredRelationsWithNotes}
              onRelationDeleted={handleRelationDeleted}
              onDelete={isCreatingCBC ? undefined : handleDeleteCBC}
              isDeleting={isDeletingCBC}
            />
          </div>
        </div>
      </div>
      </>
    // </section>
  );
};

export default EnsembleModellingV2;
